import { CreateDispatchDto, CreateDispatchsDto, DispatchExportDto, DispatchMapDto, FormDispatchDto, ResumeGraphDto, RowDispatchDto, RowDispatchReportDto } from "@/dtos";
import { Dispatch, DispatchObservation, DispatchOperator, Vehicle } from "@/entities";
import { getStatusDispatch } from "@/helpers/options";
import { ObservationType, OperatorsType, TripPatternOrientation } from "@/helpers/types";
import { dateFormatHourMinuteSecond, dateFormatYearMonthDay, getAdministratorFromLocalStorage, parseToLocalDateTimeFromUtc, parseToUtcDateTime } from "@/helpers/utilities";
import { generateColor } from "@/helpers/utilities/colors";
import { BarDataset } from "@/ui/components/graphs/bar/interfaces";
import { MapCoordinate } from "@/ui/components/maps/common/interfaces/map-coordinate";
import { MapLine } from "@/ui/components/maps/common/interfaces/map-line";


export const formatRowDispatchDto = async (dispatch: Dispatch[]): Promise<RowDispatchDto[]> => {
    const result = [] as RowDispatchDto[];
    dispatch?.map((x) => {
        const orientation = (x.details?.trip) ? getStatusDispatch(x.details.trip.orientation).label : '';
        const row: RowDispatchDto = {
            ...x,
            date_start_formatted: parseToLocalDateTimeFromUtc(x.date_start.toString()),
            date_end_formatted: parseToLocalDateTimeFromUtc(x.date_end.toString()),
            company: (x.details.company) ? x.details.company.name : 'S/N',
            route: (x.details.trip) ? x.details.trip.route.long_name : 'S/N',
            trip_pattern: (x.details.trip) ? x.details.trip.name : 'S/N',
            vehicle: (x.details.vehicle) ? x.details.vehicle.name : 'S/N',
            orientation: orientation,
            driver: (x.operators) ? x.operators[0].name : 'S/N',
            show: (x.is_show) ? 'Si' : 'No',
            checkpoints: x.checkpoints_completed + "/" + x.checkpoints_planned,
            active: x.is_active ? 'Activo' : 'Inactivo',
        }
        result.push(row);
    });

    return result;
}

export const formatDispatchDto = async (formDispatch: FormDispatchDto): Promise<CreateDispatchsDto> => {
    const result: CreateDispatchsDto = { dispatchDtos: [] };
    const administrator = getAdministratorFromLocalStorage();

    const operator: DispatchOperator = {
        id: formDispatch.id_operator,
        name: formDispatch.name_operator,
        type: OperatorsType.PRINCIPAL,
        from: parseToUtcDateTime(formDispatch.date_start + " " + formDispatch.hour_start),
        to: parseToUtcDateTime(formDispatch.date_start + " " + formDispatch.hour_start),
    }

    const observation: DispatchObservation = {
        admin_id: administrator.admin_id,
        type: ObservationType.ALERT,
        detail: formDispatch.observation ?? ''
    }

    const outboundDispatchDto: CreateDispatchDto = {
        company_id: formDispatch.id_company,
        vehicle_id: formDispatch.id_vehicle,
        route_id: formDispatch.id_route,
        trip_pattern_id: formDispatch.id_trip_pattern,
        date_start: parseToUtcDateTime(formDispatch.date_start + " " + formDispatch.hour_start),
        operators: [operator],
        observations: (formDispatch.observation) ? [observation] : undefined,
        is_show: formDispatch.is_show
    }

    result.dispatchDtos.push(outboundDispatchDto);

    if (
        formDispatch.id_route_inbound && formDispatch.id_trip_patterns_inbound &&
        formDispatch.date_start_inbound && formDispatch.hour_start_inbound
    ) {
        const inboundDispatchDto: CreateDispatchDto = {
            company_id: formDispatch.id_company,
            vehicle_id: formDispatch.id_vehicle,
            route_id: formDispatch.id_route_inbound,
            trip_pattern_id: formDispatch.id_trip_patterns_inbound,
            date_start: parseToUtcDateTime(formDispatch.date_start_inbound + " " + formDispatch.hour_start_inbound),
            operators: [operator],
            observations: (formDispatch.observation) ? [observation] : undefined,
            is_show: formDispatch.is_show
        }

        result.dispatchDtos.push(inboundDispatchDto);
    }

    return result;
}

export const formatDispatchExportDto = (dispatches: Dispatch[]): DispatchExportDto[] => {
    return dispatches.map((x) => {
        const orientation = (x.details?.trip) ? getStatusDispatch(x.details.trip.orientation).label : '';
        const row: DispatchExportDto = {
            date_start: dateFormatYearMonthDay(x.date_start),
            hour_start: dateFormatHourMinuteSecond(x.date_start),
            name_vehicle: (x.details.vehicle) ? x.details.vehicle.name : 'S/N',
            name_route: (x.details.trip) ? x.details.trip.route.long_name : 'S/N',
            checkpoints: x.tripNum + "/" + x.checkpoints_planned,
            conductor: (x.operators) ? x.operators[0].name : 'S/N',
            name_orientation: orientation,
            show: (x.is_show) ? 'Si' : 'No',
            active: x.is_active ? 'Activo' : 'Inactivo'
        }

        return row;
    });
}

export const formatRowReportDto = (dispatches: Dispatch[]): RowDispatchReportDto[] => {
    return dispatches?.map(x => {
        const row: RowDispatchReportDto = {
            ...x,
            vehicle_label: x.details?.vehicle?.name,
            date_start_label: parseToLocalDateTimeFromUtc(x.date_start.toString()),
            date_end_label: parseToLocalDateTimeFromUtc(x.date_end.toString()),
            route_label: x.details?.trip?.route?.long_name,
            trip_label: x.details?.trip?.name,
            date_start_real_label: (x?.date_start_real) ? parseToLocalDateTimeFromUtc(x.date_start_real.toString()) : '',
            date_end_real_label: (x?.date_end_real) ? parseToLocalDateTimeFromUtc(x.date_end_real.toString()) : '',
            checkpoint_label: (x.checkpoints_planned) ? x.checkpoints_completed + "/" + x.checkpoints_planned : '',
            dispatcher: x.details?.dispatcher?.name ?? 'S/N',
            driver: (x.operators?.length > 0) ? x.operators[0].name : 'S/N'
        }

        return row;
    });
}

export const formatDispatchLine = (dispatch: DispatchMapDto): MapLine => {
    if (dispatch.trip_pattern_plotted.length > 0) {
        const coords = dispatch.trip_pattern_plotted[0].coords.map((x, i) => {
            const mapCoordinate: MapCoordinate = {
                name: dispatch.id + i,
                coordinate: [x.longitude, x.latitude],
                isPrimaryColor: true
            }

            return mapCoordinate;
        });

        const name = (dispatch.trip_pattern_plotted[0].type == TripPatternOrientation.OUTBOUND) ? 'outbound' : 'inbound';
        const mapLine: MapLine = {
            identification: name,
            color: dispatch.color_route,
            coordinates: coords
        }

        return mapLine;
    }

    return {} as MapLine;
}

export const getResumeDispatch = (dispatchs: Dispatch[]) => {
    const items: ResumeGraphDto[] = [];

    const colorPlanned = generateColor();
    const planned: ResumeGraphDto = {
        style: `background-color:${colorPlanned.color}; color:${colorPlanned.textColor}`,
        total: dispatchs.length,
        name: 'Planificados'
    }
    items.push(planned);

    const colorCompleted = generateColor();
    const totalCompleted = dispatchs.filter(x => x.checkpoints_planned == x.checkpoints_completed).length;
    const completed: ResumeGraphDto = {
        style: `background-color:${colorCompleted.color}; color:${colorCompleted.textColor}`,
        total: totalCompleted,
        name: 'Cumplidos'
    }
    items.push(completed);

    const colorSanctions = generateColor();
    const sanctions: ResumeGraphDto = {
        style: `background-color:${colorSanctions.color}; color:${colorSanctions.textColor}`,
        total: 0,
        name: 'Multados'
    }
    items.push(sanctions);

    const colorDisabled = generateColor();
    const totalDisabled = dispatchs.filter(x => x.is_active == false).length;
    const disabled: ResumeGraphDto = {
        style: `background-color:${colorDisabled.color}; color:${colorDisabled.textColor}`,
        total: totalDisabled,
        name: 'Deshabilitados'
    }
    items.push(disabled);

    return items;
}

export const formatGraphLabelsDispatch = (vehicles: Vehicle[], dispatchs: Dispatch[]) => {
    const labels: string[] = [];
    vehicles.map(v => {
        const amount = dispatchs.filter(d => d.details.vehicle.name == v.name).length;
        if (amount > 0) {
            labels.push(v.name);
        }
    });
    return labels;
}

export const formatGraphDatasetsDispatch = (labels: string[], dispatchs: Dispatch[]) => {
    const amounts: number[] = [];
    const colors: string[] = [];

    for (const label of labels) {
        const amount = dispatchs.filter(d => d.details.vehicle.name == label).length;
        const color = generateColor();

        amounts.push(amount);
        colors.push(color.color);
    }

    const dataset: BarDataset = {
        label: `Despachos`,
        data: amounts,
        backgroundColor: colors
    }

    return [dataset];
}